import React, { ComponentProps, forwardRef } from 'react';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { Checkbox } from '../Checkbox/Checkbox';
import { type TokenColor, Typography } from '../Typography/Typography';
import { Embolden } from '../Embolden';
import { styled } from '../../styles';
import { TransferListOption } from './TransferList.types';
import { Draggable, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { VirtualListItemComponentProps } from '../VirtualList/VirtualList';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Tooltip } from '../Tooltip/Tooltip';

export const DEFAULT_ITEM_SIZE = 48;

interface InternalItemProps<T extends string> {
  checked: TransferListOption<T>[];
  disabledAll?: boolean;
  searchValue: string;
  onCheck: (option: TransferListOption<T>) => void;
  onLabelChange?: (option: TransferListOption<T>) => void;
  itemsWithOriginalNames?: TransferListOption<T>[];
}

type TransferListCheckboxListItemProps<T extends string> = VirtualListItemComponentProps<
  TransferListOption<T>,
  InternalItemProps<T>
>;
export function TransferListCheckboxListItem<T extends string>({
  item,
  props,
  style
}: TransferListCheckboxListItemProps<T>) {
  const { value, label, sublabel } = item ?? {};
  const { checked = [], disabledAll, searchValue, onCheck, onLabelChange } = props ?? {};
  return (
    <StyledCheckboxWrapper key={value} style={style}>
      <div className="item-container">
        <Checkbox
          dataTest={`transfer-list-checkbox-${value}`}
          checked={!!checked.find((it) => (value ?? '_UNKNOWN_') === it.value)}
          onChange={() => {
            if (!!onCheck && item) {
              onCheck?.(item);
            }
          }}
          sx={{ margin: 0, flexGrow: 1 }}
          disabled={disabledAll}
        >
          <Typography variant="label" large color="colorOnSurface">
            <Embolden term={searchValue}>{label}</Embolden>
          </Typography>
          {!!sublabel && (
            <Typography variant="body" small color="colorOnSurfaceVariant">
              {sublabel}
            </Typography>
          )}
        </Checkbox>
        {!!onLabelChange && (
          <IconButton
            className="edit-icon"
            iconName="edit"
            onClick={() => {
              if (item) {
                onLabelChange(item);
              }
            }}
          />
        )}
      </div>
    </StyledCheckboxWrapper>
  );
}
export function TransferListCheckboxListItemDraggable<T extends string>({
  item,
  props,
  style,
  index,
  isScrolling
}: TransferListCheckboxListItemProps<T>) {
  const value = item?.value || `${index}`;

  return (
    <Draggable draggableId={value} index={index} key={value}>
      {(provided, snapshot) => {
        return (
          <TransferListCheckboxListItemDraggableBase
            index={index}
            key={value}
            props={props}
            item={item}
            isScrolling={isScrolling}
            draggableProps={provided.draggableProps}
            dragHandleProps={provided.dragHandleProps}
            style={{ ...style, ...provided.draggableProps.style }}
            ref={provided.innerRef}
            snapshot={snapshot}
          />
        );
      }}
    </Draggable>
  );
}

export const TransferListCheckboxListItemDraggableBase = forwardRef<
  HTMLDivElement,
  TransferListCheckboxListItemProps<any> & {
    draggableProps: ComponentProps<'div'>;
    dragHandleProps?: Omit<ComponentProps<'div'>, 'color'> & { color?: TokenColor };
    snapshot?: DraggableStateSnapshot;
  } & { dragging?: boolean }
>(({ props, item, style, draggableProps, dragHandleProps, dragging }, ref) => {
  const { value, label, sublabel } = item ?? {};

  const { checked = [], disabledAll, searchValue, onCheck, onLabelChange, itemsWithOriginalNames } = props ?? {};
  const { intl } = useIntlBds();

  const originalOption = itemsWithOriginalNames?.find((it) => it.value === value);

  return (
    <StyledCheckboxWrapper ref={ref} {...draggableProps} key={value} style={style} $dragging={dragging}>
      <div className="item-container">
        <Icon name="drag_indicator" {...dragHandleProps} />
        <Checkbox
          dataTest={`transfer-list-checkbox-${value}`}
          checked={!!checked.find((it) => (value ?? '_UNKNOWN_') === it.value)}
          onChange={() => {
            if (!!onCheck && item) {
              onCheck(item);
            }
          }}
          sx={{ margin: 0, flexGrow: 1 }}
          disabled={disabledAll}
        >
          <Tooltip
            disabled={!originalOption || originalOption.label === label}
            title={`${intl.TransferList.DialogEditLabel.originalName}: "${originalOption?.label}"`}
          >
            <span>
              <Typography variant="label" large color="colorOnSurface">
                <Embolden term={searchValue}>{label}</Embolden>
              </Typography>
              {!!sublabel && (
                <Typography variant="body" small color="colorOnSurfaceVariant">
                  {sublabel}
                </Typography>
              )}
            </span>
          </Tooltip>
        </Checkbox>
        {!!onLabelChange && (
          <IconButton
            className="edit-icon"
            iconName="edit"
            onClick={() => {
              if (item) {
                onLabelChange(item);
              }
            }}
          />
        )}
      </div>
    </StyledCheckboxWrapper>
  );
});

const StyledCheckboxWrapper = styled('div')<{ $dragging?: boolean }>`
  padding-left: ${({ theme }) => theme.spacingSize.m};
  border-bottom: 1px solid ${({ theme }) => theme.tokens.colorSurfaceVariant};
  background-color: ${({ theme, $dragging }) => ($dragging ? theme.tokens.statePrimaryFocusStateLayerColor : undefined)};  
  [data-icon-name]{
      position: relative;
      
      &::before {
          transition: background-color 0.2s ease-in-out;
          content: ' ';
          position: absolute;
          display: block;
          top: -4px;
          right: -4px;
          bottom: -4px;
          left: -4px;
          border-radius: 50%;
          background-color: transparent;
      }
      
      &:focus {
          &::before {
              background-color: ${({ theme }) => theme.tokens.statePrimaryFocusStateLayerColor};
          }
      }
  }
    
  .item-container {
      height: 100%;
      display: flex;
      align-items: center;
      
      .MuiFormGroup-root, label {
          height: 100%;
      }
      
      .edit-icon {
          transition: opacity 0.2s ease-in-out;
          opacity: 0;
      }
      
      &:hover {
          .edit-icon {
              opacity: 1;
          }
      }
  }
    
  &:hover {
    background-color: rgba(0, 92, 185, 0.08);
  }
`;
